import React from 'react';
import {useTranslation} from 'react-i18next';

import NewImageIcon from '@edna/icons/newImage.svg';

import {ErrorText, Icon} from '../primitives';
import {TContext, TRestrictions} from './definitions';
import * as S from './style';
import {getFormatsHint} from './utils';

type TProps = {
  onChange: (value: File[] | string) => void;
  restrictions: TRestrictions;
  fieldRef: React.RefObject<HTMLInputElement>;
  context: TContext;
  error?: string;
  isInvalid?: boolean;
  readOnly?: boolean;
  name?: string;
  onBlur?: TEmptyFunction;
  isShowError?: boolean;
  isMultiple?: boolean;
};

const Image = React.memo<TProps>(
  ({
    onChange,
    context,
    readOnly,
    error,
    onBlur,
    restrictions,
    isShowError,
    isInvalid,
    isMultiple,
    fieldRef,
    name,
  }) => {
    const {t} = useTranslation();

    const handleFileChange = React.useCallback(
      (event: React.ChangeEvent) => {
        const target = event.target as HTMLInputElement;

        if (!target.files?.length) {
          return;
        }

        onChange([...target.files]);
        target.value = '';
      },
      [onChange],
    );

    const showDimensions = !context.imageInfo && !!restrictions.width && !!restrictions.height;

    const formats = getFormatsHint(restrictions);

    return (
      <S.ImageContainer>
        <S.Image onClick={onBlur} readOnly={readOnly} isInvalid={isInvalid}>
          <input
            hidden
            id={name}
            ref={fieldRef}
            type="file"
            accept={restrictions.accept ?? 'image/*'}
            multiple={isMultiple}
            onChange={handleFileChange}
          />
          {!!context.imageInfo && <img src={context.imageInfo.url} alt="Image" />}
          {!context.imageInfo && (
            <>
              <Icon as={NewImageIcon} size="48px" />
              {!!formats && <span>{formats}</span>}
            </>
          )}
        </S.Image>
        {showDimensions && (
          <S.Hint>
            {t('EdnaImagePicker:imageDimensions', {
              width: restrictions.width,
              height: restrictions.height,
            })}
          </S.Hint>
        )}
        {isShowError && !context.isLoading && error && <ErrorText>{error}</ErrorText>}
      </S.ImageContainer>
    );
  },
);

export {Image};
